import { formatToTitleCase } from "@/utils/formatters/stringFormatters";

/**
 * The eligibility stamp's reason codes (coronis-apps apps/pkg/domains/claim_item/
 * types.go, `KnownIneligibleReasons`) and their plain-language copy. One list for
 * every surface that names a reason: the line's banner (full sentence), the
 * claim-lines list's "Reason" facet (#7380, short label), and anything that
 * groups lines by reason. Unknown codes fall back to the title-cased code, so a
 * new backend reason still reads before the SPA learns it.
 */

/** Full sentences: what the reason means and what would change it. */
export const INELIGIBLE_REASON_LABELS: Record<string, string> = {
  below_deductible: "Counts toward the deductible; the claimant has not met it yet",
  not_paid: "Not a paid line (status is not Paid). Only paid lines accumulate",
  missing_paid_date: "No paid date, so it cannot be placed in a paid window",
  missing_paid_amount: "No paid amount",
  paid_date_outside_policy: "Paid outside the specific benefit's paid window (its paid dates plus any run-out)",
  service_date_outside_incurred_period:
    "Incurred outside the specific benefit's incurred window, widened by the contract basis",
  missing_service_date: "No service date, so it cannot be placed in the incurred window",
  owned_by_other_term: "Owned by another policy term (Year 1 first). It counts there, not here",
  no_claimant: "No claimant on the line",
  pending_recompute: "Recompute pending. The stamp refreshes shortly",
  not_in_deductible_basis: "Outside the deductible basis for this policy",
};

/** Two-or-three-word labels for a facet, a chip or a column. */
export const INELIGIBLE_REASON_SHORT_LABELS: Record<string, string> = {
  missing_paid_date: "Missing paid date",
  not_paid: "Not paid",
  missing_paid_amount: "Missing paid amount",
  below_deductible: "Below deductible",
  owned_by_other_term: "Owned by another term",
  paid_date_outside_policy: "Paid outside window",
  service_date_outside_incurred_period: "Incurred outside window",
  missing_service_date: "Missing service date",
  no_claimant: "No claimant",
  pending_recompute: "Recompute pending",
  not_in_deductible_basis: "Outside deductible basis",
};

export const describeIneligibleReason = (code: string): string =>
  INELIGIBLE_REASON_LABELS[code] ?? formatToTitleCase(code.replace(/_/g, " "));

export const shortIneligibleReason = (code: string): string =>
  INELIGIBLE_REASON_SHORT_LABELS[code] ?? formatToTitleCase(code.replace(/_/g, " "));

/**
 * Options for the claim-lines list's "Reason" facet (#7380 AC5), in the order a
 * reviewer works the backlog: the deductible-basis reasons that can be fixed on
 * the line first (paid date, status), then the accumulation outcomes, then the
 * window reasons, then the rest. Values are the API's `ineligible_reason` codes;
 * several selected codes are OR'd server-side (array overlap).
 */
export const INELIGIBLE_REASON_FACET_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  "missing_paid_date",
  "not_paid",
  "missing_paid_amount",
  "below_deductible",
  "owned_by_other_term",
  "paid_date_outside_policy",
  "service_date_outside_incurred_period",
  "missing_service_date",
  "no_claimant",
  "pending_recompute",
  "not_in_deductible_basis",
].map((value) => ({ value, label: shortIneligibleReason(value) }));

/**
 * Route to the claim-lines list pre-filtered to one or more reasons (#7380): what
 * the review rail's paid-date card and the Result tab's load notice link to, so a
 * reviewer lands on the backlog instead of walking the aside. `scope` narrows it
 * further (the document's client). The filter is the list's URL state, so the
 * aside shows the same ticks and exports honour it.
 */
export const claimItemsByReasonPath = (reasons: readonly string[], scope: Record<string, string> = {}): string =>
  `/claim-items?filter=${encodeURIComponent(JSON.stringify({ ...scope, ineligible_reason: [...reasons] }))}`;
